import React, { useRef, useEffect, useState } from 'react'
import Helmet from 'react-helmet'
import styled from '@emotion/styled'

import { fontFamilyOpenSans } from '../../constants/principles'
import { useGlobalState } from '../../hooks/GlobalStateProvider'
import { BrowserUtils } from '../../utils/siteUtils'
import { FlexDiv } from '../generic/Basics'

const smallDevice = BrowserUtils.isBrowser && window.innerWidth <= 1024
const touchDevice = typeof document !== 'undefined' && 'ontouchstart' in document.documentElement
const iOS = BrowserUtils.isIOS
const useClassic = false // use classic version if you want no background scroll indicator but will cause shift in header and bottom menu in iOS

const ModalClassic = ({ closeModal, modalComponent }) => {
  const isDesktop = BrowserUtils.isDesktop()
  const [isHidden, setIsHidden] = useState(!isDesktop)
  const initialWindowScrollYRef = useRef(window.scrollY)
  const initialWindowScrollY = initialWindowScrollYRef.current

  useEffect(() => {
    if (!isDesktop) {
      setTimeout(setIsHidden, 50)
    }

    return () => setTimeout(() => !isDesktop && window.scrollTo(0, initialWindowScrollY), 1) // wait for modal close
  }, [initialWindowScrollY, isDesktop])

  return (
    <>
      <Helmet>
        <style type="text/css">{`
        body {
          ${
            isDesktop
              ? `overflow: hidden; padding-right: ${window.outerWidth - window.innerWidth + 1}px;` //
              : `bottom: 0; left: 0; position: fixed; right: 0; top: -${initialWindowScrollY}px;`
          }
        }
        `}</style>
      </Helmet>
      <ModalDiv onClick={closeModal}>{!isHidden && <ModalContentDiv onClick={e => e.stopPropagation()}>{modalComponent}</ModalContentDiv>}</ModalDiv>
    </>
  )
}

const ModalSpecial = ({ closeModal, modalComponent, showBackgroundContent, isIframe, closeOnClickOutside }) => {
  const { globalState } = useGlobalState()
  const { height, frameScrollPosition, viewportHeight, frameHeight } = globalState
  const initWinScroll = BrowserUtils.isBrowser && window.scrollY
  const shouldAllowModalScroll = (touchDevice && smallDevice) || isIframe
  const scrollLimit = isIframe ? '100vh' : '1px'
  const { modalBackgroundRef, modalRef, modalScrollRef } = useScrollBlock(closeModal, height, initWinScroll, scrollLimit, isIframe)

  const top = Math.max(0, frameHeight - 600 > frameScrollPosition ? frameScrollPosition : frameHeight - 600)
  const bottom = Math.max(0, frameHeight - frameScrollPosition - viewportHeight)

  return (
    <BackgroundDiv id="bgModalDiv">
      <ModalDiv ref={modalBackgroundRef} onClick={closeOnClickOutside ? closeModal : () => {}} top={top || 0} bottom={bottom || 0}>
        {shouldAllowModalScroll ? (
          <ModalScrollDiv ref={modalScrollRef}>
            <ModalScrollContentDiv scrollLimit={scrollLimit}>
              <ModalContentDiv ref={modalRef} onClick={e => e.stopPropagation()} showBackgroundContent={showBackgroundContent}>
                {modalComponent}
              </ModalContentDiv>
            </ModalScrollContentDiv>
          </ModalScrollDiv>
        ) : (
          <ModalContentDiv ref={modalRef} onClick={e => e.stopPropagation()} showBackgroundContent={showBackgroundContent}>
            {modalComponent}
          </ModalContentDiv>
        )}
      </ModalDiv>
    </BackgroundDiv>
  )
}

const Modal = useClassic ? ModalClassic : ModalSpecial

export default ({ children, name = 'generic', open, dependencyList = [], closeOnClickOutside }) => {
  const { globalState, updateGlobalState, globalVariables, updateGlobalVariables } = useGlobalState()
  const { modalCount = 0, showModal } = globalState
  const modalName = typeof showModal !== 'string' ? showModal && showModal.name : showModal
  const modalNoRef = useRef()

  useEffect(() => {
    if (!modalNoRef.current.afterfirst) {
      modalNoRef.current.afterfirst = true
    } else if (dependencyList && dependencyList.length && modalName === name) {
      updateGlobalState({ modalCount: modalCount + 1 })
    }
    // eslint-disable-next-line
  }, dependencyList)

  const isEffect = modalNoRef.current && modalNoRef.current.modalCount !== modalCount
  const afterfirst = modalNoRef.current && modalNoRef.current.afterfirst
  const firstTime = !globalVariables.current
  const openChanged = !modalNoRef.current || modalNoRef.current.open !== open
  const forceShow = open && (firstTime || openChanged)
  const forceHide = !open && openChanged && modalName === name
  const doShowModal = (modalName === name && !forceHide) || forceShow

  modalNoRef.current = { modalCount, open, afterfirst }

  if (!isEffect && doShowModal) {
    updateGlobalVariables({ modalContent: children, closeOnClickOutside: closeOnClickOutside })

    if (forceShow) {
      updateGlobalState({ modalCount: modalCount + 1, showModal: name })
    } else if (showModal !== name) {
      updateGlobalState({ modalCount: modalCount + 1 })
    }
  } else if (forceHide) {
    updateGlobalState({ showModal: false })
  }

  return null
}

export const ModalContainer = ({ isIframe }) => {
  const {
    globalVariables,
    globalState: { showModal },
    updateGlobalState
  } = useGlobalState()

  // Hack: to allow exitstitial to have different background than typical modal.
  const showBackgroundContent = showModal !== 'Exitstitial'

  return showModal && globalVariables.current ? (
    <Modal
      isIframe={isIframe}
      closeModal={e => updateGlobalState({ showModal: false })}
      modalComponent={globalVariables.current.modalContent}
      showBackgroundContent={showBackgroundContent}
      closeOnClickOutside={globalVariables.current.closeOnClickOutside}
    />
  ) : null
}

const useScrollBlock = (closeModal, winHeight, winScroll, scrollLimit, isIframe) => {
  const modalBackgroundRef = useRef(null)
  const modalRef = useRef(null)
  const modalScrollRef = useRef(null)
  const winRef = useRef({ winHeight, winScroll })

  useEffect(() => {
    let mounted = true
    let actionNo = 0
    const checkAction = (action, thisno, i) => !action() && setTimeout(() => i < 50 && thisno === actionNo && checkAction(action, thisno, i + 1), 50)
    let hasTouchMove = false
    const modalTouchMove = e => (hasTouchMove = true) && e.stopPropagation()
    const modalTouchEnd = e => (hasTouchMove = false)

    modalRef.current.addEventListener('touchmove', modalTouchMove, { passive: false })
    modalRef.current.addEventListener('touchend', modalTouchEnd, { passive: false })
    modalBackgroundRef.current.addEventListener('touchmove', e => e.preventDefault(), { passive: false })

    // Used on mobile and iframeScroll, allows the modal to scroll and close or just scroll (in the case of iframe)
    if (modalScrollRef.current) {
      const initScroll = scrollLimit.replace('px', '') / 2

      !isIframe && modalScrollRef.current.scrollTo(0, initScroll)
      isIframe && modalRef.current.scrollIntoView({ block: 'start', inline: 'start', behavior: 'smooth' })

      let lastScroll = initScroll

      const fixScroll = () => {
        if (!mounted) {
          return true
        }

        let done = false

        if (Math.abs(initScroll - modalScrollRef.current.scrollTop) > winRef.current.winHeight / 2 - (iOS ? 0 : 100)) {
          !hasTouchMove && (done = true) && closeModal()
        } else if (modalScrollRef.current.scrollTop !== initScroll && lastScroll === modalScrollRef.current.scrollTop) {
          !hasTouchMove && (done = true) && modalScrollRef.current.scrollTo(0, initScroll)
        }

        lastScroll = modalScrollRef.current.scrollTop
        return done
      }

      const onScroll = e => checkAction(fixScroll, ++actionNo, 0)
      !isIframe && modalScrollRef.current.addEventListener('scroll', onScroll)
    }

    let lastWinScroll = winRef.current.winScroll

    const fixWinScroll = () => {
      let done = false

      if (window.scrollY !== winRef.current.winScroll && lastWinScroll === window.scrollY) {
        !hasTouchMove && (done = true) && window.scrollTo(0, winRef.current.winScroll)
      }

      lastWinScroll = window.scrollY
      return done
    }

    // Prevents normal content from scrolling
    const onWinScroll = e => (touchDevice ? checkAction(fixWinScroll, ++actionNo, 0) : window.scrollTo(0, winRef.current.winScroll))
    window.addEventListener('scroll', onWinScroll)

    return () => {
      window.removeEventListener('scroll', onWinScroll)
      mounted = false
    }
  }, [closeModal, isIframe, scrollLimit])

  return { modalBackgroundRef, modalRef, modalScrollRef }
}

const ModalScrollDiv = styled.div`
  overflow: auto;
  height: 100%;
  width: 100%;
  -webkit-overflow-scrolling: touch;
  text-align: center;
`
const ModalScrollContentDiv = styled(FlexDiv)`
  height: calc(100% + ${p => p.scrollLimit});
  width: 100%;
`
const ModalContentDiv = styled(FlexDiv)`
  ${p =>
    p.showBackgroundContent &&
    `-webkit-overflow-scrolling: touch;
  background-color: white;
  text-align: center;`}
`
const ModalDiv = styled(FlexDiv)`
  position: fixed;
  top: ${p => p.top}px;
  right: 0;
  left: 0;
  bottom: ${p => p.bottom}px;
  z-index: 10;
  border-style: none;
  font-family: ${fontFamilyOpenSans};
  -webkit-text-size-adjust: 100%;
  transition: top 1s ease;
`
const BackgroundDiv = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  z-index: 10;
  background-color: rgba(0, 0, 0, 0.6);
`
